import get from 'lodash/get'
import moment from 'moment'
import {linearRegression} from 'simple-statistics'
import {getCurrentTimePeriod} from 'redux/modules/currentUser/selectors'
import eachDay from 'utils/eachDay'

const formatDate = date => moment(date).format('YYYY-MM-DD')
const startOfPeriod = range => moment(range.start.start).toDate()
const endOfPeriod = range => moment(range.start.end).toDate()
const pastDateForPeriod = (range, index) => moment(range.end.start).clone().add(index, 'days')
const getCurrentDayCount = (today, range) => moment(today).diff(startOfPeriod(range), 'days')
const getPastDayCount = range => moment(endOfPeriod(range)).diff(startOfPeriod(range), 'days')
const predictionDateForPeriod = (date, timePeriod) => {
  switch (timePeriod) {
    case 'year':
      return moment(date).clone().subtract(26, 'weeks').toDate()

    case 'month':
      return moment(date).clone().subtract(2, 'months').toDate()

    case 'week':
      return moment(date).clone().isoWeek(moment(date).isoWeek() - 1).toDate()
  }
}

export function calculatePeriodData(
  today,
  timePeriod,
  data,
  fields,
  unAccumulatedFields = [],
  customRanges,
  totals
) {
  const accumulators = {}
  const accumulate = (id, value = 0) => {
    accumulators[id] = accumulators[id] ? accumulators[id] + value : value
    return accumulators[id]
  }
  const predictions = {}
  const addPredictionValue = (name, value) => {
    if (!predictions[name]) {
      predictions[name] = []
    }
    predictions[name].push([predictions[name].length, value])
  }

  const getValuesFromDate = (date, postfix = '') => fields.reduce((acc, fieldName) => {
    if (!moment(date).isAfter(today)) {
      const value = get(data, [date, fieldName], 0)
      const accValue = accumulate(fieldName + postfix, value)
      acc[fieldName + postfix] = value
      acc[fieldName + 'Sum' + postfix] = accValue

      const orderCount = get(data, [date, 'orderCount'], 0)
      const costValue = get(data, [date, 'costValue'], 0)
      acc['CPAValue' + postfix] = (costValue != 0 && orderCount != 0) ? costValue / orderCount : 0
      const costValueSum = acc['costValueSum' + postfix]
      const orderCountSum = acc['orderCountSum' + postfix]
      acc['CPAValueSum' + postfix] = (costValueSum != 0 && orderCountSum != 0) ? costValueSum / orderCountSum : 0
    }
    return acc
  }, {})

  const startOfPeriodDate = startOfPeriod(customRanges)
  const endOfPeriodDate = endOfPeriod(customRanges)
  const applyPredictions = moment(today).isBetween(startOfPeriodDate, endOfPeriodDate, null, '[]')

  const result = eachDay(startOfPeriodDate, endOfPeriodDate).map((date, index) => {
    const datePast = pastDateForPeriod(customRanges, index)
    const currentValues = getValuesFromDate(formatDate(date))
    const pastValues = getValuesFromDate(formatDate(datePast), 'Past')
    return {
      date: formatDate(date),
      datePast: formatDate(datePast),
      ...currentValues,
      ...pastValues
    }
  })

  if (applyPredictions) {
    eachDay(predictionDateForPeriod(today, timePeriod), today).forEach(date => {
      fields.forEach(fieldName => {
        const value = get(data, [formatDate(date), fieldName])
        addPredictionValue(fieldName, accumulate(fieldName + 'SumRegression', value))
      })
    })
  }

  const calculatedValues = {}
  fields.forEach(fieldName => {
    if (applyPredictions && !unAccumulatedFields.includes(fieldName) && predictions[fieldName]) {
      const {m, b} = linearRegression(predictions[fieldName])

      const x1 = result.findIndex(item => moment(item.date).isSame(today, 'day'))
      if (x1 !== -1) {
        const y1 = result[x1][`${fieldName}Sum`]
        const lineFunction = x => m * x + b
        const offset = lineFunction(x1) - y1

        result.forEach((item, index) => {
          if (!moment(item.date).isBefore(today)) {
            item[`${fieldName}SumRegression`] = lineFunction(index) - offset
          }
        })
      }
    }

    if (totals) {
      const current = totals.current && totals.current[fieldName]
      const previous = (totals.previous && totals.previous[fieldName]) || 0
      const absolute = current - previous

      calculatedValues[`${fieldName}AbsoluteSumChange`] = absolute
      calculatedValues[`${fieldName}RelativeSumChange`] = (current && previous)
        ? absolute / Math.max(current, previous)
        : undefined
      calculatedValues[`${fieldName}Sum`] = current
      calculatedValues[`${fieldName}SumPast`] = previous
      calculatedValues[`${fieldName}Average`] = current / getCurrentDayCount(today, customRanges)
      calculatedValues[`${fieldName}PastAverage`] = previous / getPastDayCount(customRanges)
    }
  })

  return {
    values: result,
    calculatedValues
  }
}

const fields = ['regCount', 'ftdCount', 'orderCount', 'orderValue', 'costValue', 'CPAValue']
export function getCurrentPeriodData(state, timePeriodRanges) {
  if (!state.dashboard.projection) return null
  const {today} = state
  const timePeriod = getCurrentTimePeriod(state)
  if (!timePeriod || !timePeriodRanges.start || !timePeriodRanges.end) return null

  const {projection} = state.dashboard
  const {values} = calculatePeriodData(
    today,
    timePeriod,
    projection,
    fields,
    [],
    timePeriodRanges
  )
  return values
}

export function getCurrentPeriodTraffic(state, customRanges) {
  if (!state.dashboard.byDays) return null

  const {today, dashboard: {byDays}} = state
  if (!customRanges) return null
  const {start: startRange, end: endRange} = customRanges
  const timePeriod = getCurrentTimePeriod(state)
  if (!timePeriod || !startRange || !endRange) return null

  const days = eachDay(startOfPeriod(customRanges), endOfPeriod(customRanges))
  const getValue = (channelGroupKey, date, field) => get(byDays, [channelGroupKey, formatDate(date), field], 0)
  const avg = arr => arr.reduce((sum, val) => sum + val, 0) / arr.length
  return Object.keys(byDays).map(channelGroupKey => {
    let totalTraffic = []
    let totalTrafficPast = []
    let totalOrders = []
    let totalOrdersPast = []

    const result = {
      groupId: channelGroupKey,
      items: days.map((date, index) => {
        const datePast = pastDateForPeriod(customRanges, index)
        const traffic = moment(today).isBefore(date) ? undefined : getValue(channelGroupKey, date, 'trafficCount')
        const trafficPast = getValue(channelGroupKey, datePast, 'trafficCount')

        if (!moment(today).isBefore(date)) {
          totalTraffic.push(traffic)
          totalOrders.push(getValue(channelGroupKey, date, 'orderCount'))
        }
        totalTrafficPast.push(trafficPast)
        totalOrdersPast.push(getValue(channelGroupKey, datePast, 'orderCount'))

        const resultObject = {
          date,
          traffic,
          trafficPast
        }
        return resultObject
      })
    }

    return {
      ...result,
      avg: {
        traffic: avg(totalTraffic),
        trafficPast: avg(totalTrafficPast),
        orders: avg(totalOrders),
        ordersPast: avg(totalOrdersPast)
      }
    }
  })
}

export function getLastOrders(state) {
  if (!state.dashboard.lastOrders) return null

  return state.dashboard.lastOrders
}

export function getCurrentMonthDataFetching(state) {
  const {
    projectionFetching,
    byDaysFetching,
    lastOrdersFetching
  } = state.dashboard

  return projectionFetching || byDaysFetching || lastOrdersFetching
}



// WEBPACK FOOTER //
// ./client/redux/modules/dashboard/selectors.js